import { EventEmitter, Injectable, signal } from '@angular/core';

export type SpinnerKey = string;
export type SpinnerKeys = SpinnerKey | readonly SpinnerKey[];

interface SpinnerEvent {
  event: 'rv-spinner:start' | 'rv-spinner:stop';
  targetSpinner: SpinnerKey;
}

@Injectable({
  providedIn: 'root',
})
export class LoadingService {
  public readonly emitter = new EventEmitter<SpinnerEvent>();
  private readonly globalLoadingState = signal(false);

  private readonly globalSpinnerKeyCounts = new Map<SpinnerKey, number>();

  public readonly isGlobalLoading = this.globalLoadingState.asReadonly();

  start(spinnerKeys: SpinnerKeys): void {
    for (const spinnerKey of this.toArray(spinnerKeys)) {
      this.emitter.emit({
        event: 'rv-spinner:start',
        targetSpinner: spinnerKey,
      });
    }
  }

  stop(spinnerKeys: SpinnerKeys): void {
    for (const spinnerKey of this.toArray(spinnerKeys)) {
      this.emitter.emit({
        event: 'rv-spinner:stop',
        targetSpinner: spinnerKey,
      });
    }
  }

  startGlobal(spinnerKeys: SpinnerKeys): void {
    for (const spinnerKey of this.toArray(spinnerKeys)) {
      const count = this.globalSpinnerKeyCounts.get(spinnerKey) ?? 0;
      this.globalSpinnerKeyCounts.set(spinnerKey, count + 1);
    }
    this.updateGlobalLoadingState();
  }

  stopGlobal(spinnerKeys: SpinnerKeys): void {
    for (const spinnerKey of this.toArray(spinnerKeys)) {
      const count = this.globalSpinnerKeyCounts.get(spinnerKey);

      if (count === 1) {
        this.globalSpinnerKeyCounts.delete(spinnerKey);
      } else if (count !== undefined) {
        this.globalSpinnerKeyCounts.set(spinnerKey, count - 1);
      }
    }
    this.updateGlobalLoadingState();
  }

  private toArray(spinnerKeys: SpinnerKeys): readonly SpinnerKey[] {
    return typeof spinnerKeys === 'string' ? [spinnerKeys] : spinnerKeys;
  }

  private updateGlobalLoadingState(): void {
    this.globalLoadingState.set(this.globalSpinnerKeyCounts.size > 0);
  }
}
